import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { getByPath } from '../dataPaths';
import { SelectReportData, selectSectionStartPages, selectPageSlice } from '../Selector';
import { reportPageCount } from '../../../redux/actions';

// Must mirror the page geometry used by the other paginating sections so the
// outline splits at the same usable content height.
const PAGE_HEIGHT = 1123;
const PADDING_TOP = 64;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
const SAFETY_BUFFER = 4;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT - SAFETY_BUFFER; // 969px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px

// Outline entries are listed in this fixed order; each one mirrors the live
// `sectionHeading` of the page it points to, so editing a page heading keeps
// the outline in sync. Page numbers are resolved at render time from the
// running page count, so multi-page sections shift the rest accordingly.
//
// `source` is the section key used for the live heading + page number.
// `contentKey` is the key under `outlineOfThisReport.content` holding the
// editable description for that row. Keeping them explicit (instead of zipping
// the sources against Object.entries(content) by position) lets the trend
// variant insert extra rows without the two lists drifting out of alignment.
const STANDARD_OUTLINE_SECTIONS = [
  { source: 'circumplexData', contentKey: 'Results Summary Help' },
  { source: 'topStrengths', contentKey: 'Top Strengths' },
  { source: 'strenghtComments', contentKey: 'Examples Of Strengths' },
  { source: 'keyDevelopmentAreas', contentKey: 'Top Development Areas' },
  { source: 'devlopmentAreasComments', contentKey: 'Development Areas Comments' },
  { source: 'twoByTwoMatrix', contentKey: '2x2 Matrix' },
  { source: 'wordsOfWisdom', contentKey: 'Words Of Wisdom' },
  { source: 'ratingsOnEachBehavior', contentKey: 'Appendix: Ratings On Each Behavior' },
];

// Trend report adds Key Trends (after Development Areas Comments) and the
// Score-shift Appendix (after the 2x2 Matrix), matching PAGE_ORDER so the
// numbering lines up. These insertions shift the sections after them.
const TREND_OUTLINE_SECTIONS = [
  { source: 'circumplexData', contentKey: 'Results Summary Help' },
  { source: 'topStrengths', contentKey: 'Top Strengths' },
  { source: 'strenghtComments', contentKey: 'Examples Of Strengths' },
  { source: 'keyDevelopmentAreas', contentKey: 'Top Development Areas' },
  { source: 'devlopmentAreasComments', contentKey: 'Development Areas Comments' },
  { source: 'trendGainLoss', contentKey: 'Key Trends' },
  { source: 'twoByTwoMatrix', contentKey: '2x2 Matrix' },
  { source: 'appendixForApplied', contentKey: 'Appendix: Score Shift From T1 To T2' },
  { source: 'wordsOfWisdom', contentKey: 'Words Of Wisdom' },
  { source: 'ratingsOnEachBehavior', contentKey: 'Appendix: Ratings On Each Behavior' },
];

function getOutlineSections(reportType) {
  return reportType === 'trend' ? TREND_OUTLINE_SECTIONS : STANDARD_OUTLINE_SECTIONS;
}

const TITLE_CASE_MINOR_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or',
  'the', 'to', 'vs', 'via',
]);

function stripHtml(html) {
  if (html == null) return '';
  return String(html).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

function toTitleCase(str) {
  return str
    .toLowerCase()
    .split(' ')
    .map((word, i) =>
      i !== 0 && TITLE_CASE_MINOR_WORDS.has(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}

function OutlineRow({ contentKey, index, title, page, description }) {
  return (
    <div className="outline-item">
      <div className="outline-item__badge">{index + 1}</div>
      <div className="outline-item__content">
        <h4 className="outline-item__title">{title}</h4>
        <EditableText
          as="p"
          className="outline-item__desc"
          path={`outlineOfThisReport.content[${contentKey}]`}
          variant="inline"
          stripInlineWrap
          fallback={description}
        />
      </div>
      {page != null && <div className="outline-item__page">{page}</div>}
    </div>
  );
}

function HowToUseBlock() {
  return (
    <div className="outline-how-to-use">
      <EditableText
        as="h4"
        className="outline-how-to-use__heading"
        path="outlineOfThisReport.howToUseHeading"
        variant="inline"
        stripInlineWrap
      />
      <EditableText
        as="div"
        className="outline-how-to-use__list"
        path="outlineOfThisReport.howToUseTips"
        variant="block"
      />
    </div>
  );
}

function OutlinePage({ data, personName, sections, sectionHeadings, sectionPages, pageNumber, slice, reportPageCount }) {
  const content = data.content || {};

  // Precompute each row's display fields once — used by both the measurement
  // pass and the final render so the two stay identical.
  const rows = sections.map(({ contentKey }, idx) => {
    const heading = sectionHeadings[idx];
    return {
      contentKey,
      index: idx,
      title: heading ? toTitleCase(heading) : contentKey,
      page: sectionPages[idx],
      description: content[contentKey],
    };
  });

  const measureRef = useRef(null);
  // `pages` is an array of page descriptors: { rowIdxs: number[], howToUse: bool }.
  const [pages, setPages] = useState(null);

  // Re-measure when the editable slice changes (heading / description / tips
  // edits can change heights) or when the row set changes (report type).
  const isFirstSliceRef = useRef(true);
  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setPages(null);
  }, [slice, sections.length]);

  useLayoutEffect(() => {
    if (pages) return;
    if (!measureRef.current) return;
    const container = measureRef.current;

    const outerHeight = (el) => {
      if (!el) return 0;
      let h = el.getBoundingClientRect().height;
      const cs = window.getComputedStyle(el);
      h += (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
      return h;
    };

    const headingH = outerHeight(container.querySelector('[data-ol="heading"]'));
    const rowHs = rows.map((_, idx) =>
      outerHeight(container.querySelector(`[data-ol-row="${idx}"]`))
    );
    const howToUseH = outerHeight(container.querySelector('[data-ol="howtouse"]'));

    // Page 1 reserves the section heading; later pages get the full budget.
    const outPages = [];
    let curRows = [];
    let curUsed = 0;
    let isFirstPage = true;
    const avail = () => (isFirstPage ? CONTENT_HEIGHT - headingH : CONTENT_HEIGHT);

    rows.forEach((_, idx) => {
      const h = rowHs[idx] || 0;
      if (curRows.length > 0 && curUsed + h > avail()) {
        outPages.push({ rowIdxs: curRows, howToUse: false });
        curRows = [idx];
        curUsed = h;
        isFirstPage = false;
      } else {
        curRows.push(idx);
        curUsed += h;
      }
    });
    if (curRows.length > 0) {
      outPages.push({ rowIdxs: curRows, howToUse: false });
    }
    if (outPages.length === 0) {
      outPages.push({ rowIdxs: [], howToUse: false });
    }

    // Place the "How to Use" block after the last row. Keep it whole: if it does
    // not fit under the remaining space on the last page, move it to a new page.
    const last = outPages[outPages.length - 1];
    const lastUsed = (last.rowIdxs || []).reduce((s, i) => s + (rowHs[i] || 0), 0);
    const lastAvail = outPages.length === 1 ? CONTENT_HEIGHT - headingH : CONTENT_HEIGHT;
    if (lastUsed + howToUseH > lastAvail && last.rowIdxs.length > 0) {
      outPages.push({ rowIdxs: [], howToUse: true });
    } else {
      last.howToUse = true;
    }

    setPages(outPages);
    if (reportPageCount) reportPageCount('outlineOfThisReport', outPages.length);
  }, [pages, rows, reportPageCount]);

  const heading = (
    <EditableText
      as="h2"
      className="pdf-section-heading"
      path="outlineOfThisReport.sectionHeading"
      variant="inline"
      stripInlineWrap
    />
  );

  // ── Phase 1: measurement ────────────────────────────────────────────────────
  if (!pages) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
        <div
          ref={measureRef}
          style={{ position: 'absolute', top: 0, left: 0, width: CONTENT_WIDTH, visibility: 'hidden', pointerEvents: 'none' }}
        >
          <div data-ol="heading">{heading}</div>
          <div className="outline-list">
            {rows.map((row) => (
              <div key={row.contentKey} data-ol-row={row.index}>
                <OutlineRow {...row} />
              </div>
            ))}
          </div>
          <div data-ol="howtouse">
            <HowToUseBlock />
          </div>
        </div>
      </div>
    );
  }

  // ── Phase 2: render pages ───────────────────────────────────────────────────
  return (
    <>
      {pages.map((pg, pageIdx) => (
        <PageWrapper key={pageIdx} pageNumber={pageNumber + pageIdx} personName={personName}>
          {pageIdx === 0 && heading}
          {pg.rowIdxs.length > 0 && (
            <div className="outline-list">
              {pg.rowIdxs.map((idx) => (
                <OutlineRow key={rows[idx].contentKey} {...rows[idx]} />
              ))}
            </div>
          )}
          {pg.howToUse && <HowToUseBlock />}
        </PageWrapper>
      ))}
    </>
  );
}

const mapStateToProps = (state, ownProps) => {
  const reportData = SelectReportData(state);
  const startPages = selectSectionStartPages(state);
  const sections = getOutlineSections(ownProps.reportType);
  const sectionHeadings = sections.map(({ source }) =>
    stripHtml(getByPath(reportData, `${source}.sectionHeading`))
  );
  const sectionPages = sections.map(({ source }) => startPages[source]);
  return { sections, sectionHeadings, sectionPages, slice: selectPageSlice(state, 'outlineOfThisReport') };
};

const mapDispatchToProps = (dispatch) => ({
  reportPageCount: (sectionKey, count) => dispatch(reportPageCount(sectionKey, count)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OutlinePage);
